import { type ReactNode, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import {
  EMPLOYMENT_TYPE_LABELS,
  type EmploymentType,
  type PersonnelEmploymentRecord,
} from '../personnelTypes';
import { samplePreviousEmployment } from '../utils/samplePreviousEmployment';

/** Unanswered is a third state, and the form refuses to save in it. */
type SectorAnswer = '' | 'government' | 'private';

type StepKey = 'sector' | 'details';

interface Step {
  key: StepKey;
  label: string;
  valid: boolean;
  content: ReactNode;
}

/**
 * Records or corrects one PREVIOUS employer's line of employment history — CS Form 212, section V.
 *
 * **A post at THIS organization is not recorded here** — that is `<EmploymentRecordModal>`, under
 * `personnel.assign`, because it fills a plantilla slot. This form cannot name an item, so it
 * stays on `personnel.update`. (Merged 27 Oct 2026 from the service record and work experience.)
 *
 * **No picker on this form reads a catalog, and that is the design rather than an omission.**
 * The title, the employer, the grade and the step are typed, because the line records what an
 * employee stated about an employer that is not this one. The one exception is the status of
 * appointment, a `<Select>` over the product's existing `EmploymentType` vocabulary.
 *
 * **The end date is REQUIRED.** A previous employer was left; an OPEN line means a post held
 * here, and every reader asking "is this person currently appointed" depends on that.
 *
 * **The CREATE form is stepped; the EDIT form is not.** Government or private frames everything
 * after it — length of government service is counted off it — so it is asked on its own first.
 * Correcting a stored row is this card's dedicated edit, so `editing` decides and it is flat.
 */
export function PreviousEmploymentModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. Only ever a previous employer's line — a post here has no edit. */
  editing: PersonnelEmploymentRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState(editing?.effectiveFrom ?? '');
  const [effectiveTo, setEffectiveTo] = useState(editing?.effectiveTo ?? '');
  const [positionTitle, setPositionTitle] = useState(editing?.positionTitle ?? '');
  const [agencyName, setAgencyName] = useState(editing?.agencyName ?? '');
  const [monthlySalary, setMonthlySalary] = useState(
    editing?.monthlySalary == null ? '' : String(editing.monthlySalary),
  );
  const [salaryGrade, setSalaryGrade] = useState(editing?.salaryGrade ?? '');
  const [stepIncrement, setStepIncrement] = useState(editing?.stepIncrement ?? '');
  const [appointmentStatus, setAppointmentStatus] = useState<EmploymentType | null>(
    editing?.appointmentStatus ?? null,
  );
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  /*
    Seeded EMPTY on a create, never to "private". False is not a neutral default on this field —
    it is the claim that the position was private employment, and length of government service
    is counted from these rows. The server refuses an unanswered request for the same reason.
  */
  const [sector, setSector] = useState<SectorAnswer>(
    editing === null ? '' : editing.isGovernmentService ? 'government' : 'private',
  );

  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /** Create is stepped; correcting a stored row is not. See the component's doc comment. */
  const stepped = editing === null;

  const today = todayCalendarDate();

  const salaryValue = monthlySalary.trim() === '' ? null : Number(monthlySalary);
  const salaryIsNumber = salaryValue === null || Number.isFinite(salaryValue);
  const salaryNotNegative = salaryValue === null || salaryValue >= 0;

  const endsBeforeStart = effectiveTo !== '' && effectiveFrom !== '' && effectiveTo < effectiveFrom;

  const sectorValid = sector !== '';

  const detailsValid =
    positionTitle.trim().length > 0 &&
    agencyName.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    effectiveTo.length > 0 &&
    appointmentStatus !== null &&
    salaryIsNumber &&
    salaryNotNegative &&
    !endsBeforeStart;

  const valid = sectorValid && detailsValid;

  /*
    Both field blocks are built once and placed by whichever shape is rendering — the stepped
    create puts them on their own steps, the flat edit stacks them. Writing the fields out twice
    is the drift the golden rule names.
  */
  const sectorFields = (
    <RadioGroup<SectorAnswer>
      id="previous-employment-is-government-service"
      label="Government or private"
      required
      description="Whether the position was in public or government employment, or private. Length of government service is counted from this answer."
      /*
        No default. Defaulting to Private would record private employment on every line nobody
        looked at, and years of government service would quietly go missing.
      */
      value={sector}
      onChange={setSector}
      // An option card carrying a description takes the full row (frontend-forms.md).
      columns={1}
      options={[
        {
          value: 'government',
          label: 'Government',
          description: 'A department, agency, office, LGU or GOCC — public employment.',
        },
        {
          value: 'private',
          label: 'Private',
          description: 'A company, a private school or hospital, an NGO — private employment.',
        },
      ]}
      error={sector === '' ? 'Choose government or private.' : error?.fieldError('isGovernmentService')}
      disabled={saving}
    />
  );

  const detailsFields = (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="previous-employment-position-title"
          label="Position title"
          required
          description="Written in full, as it was held. Not a title from the position catalog — this records another employer's job."
          value={positionTitle}
          onChange={(e) => setPositionTitle(e.target.value)}
          error={positionTitle.trim() ? error?.fieldError('positionTitle') : 'Enter the position title.'}
          disabled={saving}
        />

        <TextField
          id="previous-employment-agency-name"
          label="Department, agency, office or company"
          required
          description="Written in full, as the form asks."
          value={agencyName}
          onChange={(e) => setAgencyName(e.target.value)}
          error={agencyName.trim() ? error?.fieldError('agencyName') : 'Enter the employer.'}
          disabled={saving}
        />

        <DateField
          id="previous-employment-effective-from"
          label="From"
          required
          max={today}
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          error={effectiveFrom ? error?.fieldError('effectiveFrom') : 'Choose the day it began.'}
          disabled={saving}
        />

        <DateField
          id="previous-employment-effective-to"
          label="To"
          required
          max={today}
          description="The last day held. A post still held at this organization is recorded as a post, not here."
          value={effectiveTo}
          onChange={setEffectiveTo}
          error={
            !effectiveTo
              ? 'Choose the last day held.'
              : endsBeforeStart
                ? 'Choose a date on or after the day it began.'
                : error?.fieldError('effectiveTo')
          }
          disabled={saving}
        />

        <TextField
          id="previous-employment-salary-grade"
          label="Salary Grade, job or pay grade"
          description="Text, as the employer expressed it — 24, Band B. Not linked to the salary schedule: a grade only means something alongside the schedule that defined it, and that one has moved since."
          value={salaryGrade}
          onChange={(e) => setSalaryGrade(e.target.value)}
          error={error?.fieldError('salaryGrade')}
          disabled={saving}
        />

        <TextField
          id="previous-employment-step-increment"
          label="Step or increment"
          description="The step within that grade — 1, Step 3."
          value={stepIncrement}
          onChange={(e) => setStepIncrement(e.target.value)}
          error={error?.fieldError('stepIncrement')}
          disabled={saving}
        />

        <TextField
          id="previous-employment-monthly-salary"
          label="Monthly salary"
          type="number"
          min={0}
          step="0.01"
          description="What was actually paid, as stated. Never read from the salary schedule."
          value={monthlySalary}
          onChange={(e) => setMonthlySalary(e.target.value)}
          error={
            !salaryIsNumber
              ? 'Enter a monthly salary.'
              : !salaryNotNegative
                ? 'Enter a monthly salary of 0 or more.'
                : error?.fieldError('monthlySalary')
          }
          disabled={saving}
        />

        <Select
          id="previous-employment-appointment-status"
          label="Status of appointment"
          required
          description="How the position was held — permanent, temporary, casual, contractual."
          value={appointmentStatus === null ? null : String(appointmentStatus)}
          onChange={(next) => setAppointmentStatus(next === null ? null : (Number(next) as EmploymentType))}
          options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          // The form's own order — most to least secure tenure — not alphabetical.
          sort="none"
          error={
            appointmentStatus === null
              ? 'Choose a status of appointment.'
              : error?.fieldError('appointmentStatus')
          }
          disabled={saving}
        />
      </div>

      {/* A free note about the whole line: full width, a sibling of the grid, never inside it. */}
      <TextField
        id="previous-employment-remarks"
        label="Remarks"
        description="Why the line ended, or anything else worth saying about it."
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        error={error?.fieldError('remarks')}
        disabled={saving}
      />
    </div>
  );

  /*
    No Review step: a stepped create form does not end on a summary (frontend-forms.md). The
    confirm tick moves to the last step with it.
  */
  const steps: Step[] = [
    { key: 'sector', label: 'Sector', valid: sectorValid, content: sectorFields },
    { key: 'details', label: 'Details', valid: detailsValid, content: detailsFields },
  ];

  const stepIndex = Math.min(step, steps.length - 1);
  const isLastStep = stepIndex === steps.length - 1;

  /** The flat edit form has no last step to reach, so the tick is always offered there. */
  const confirmShowing = !stepped || isLastStep;

  const canSave = valid && confirmed && !saving;

  /** Fills every field, so the primary action enables on the first try. */
  function generate() {
    const sample = samplePreviousEmployment();

    setEffectiveFrom(sample.effectiveFrom);
    setEffectiveTo(sample.effectiveTo);
    setPositionTitle(sample.positionTitle);
    setAgencyName(sample.agencyName);
    setMonthlySalary(sample.monthlySalary);
    setSalaryGrade(sample.salaryGrade);
    setStepIncrement(sample.stepIncrement);
    setAppointmentStatus(sample.appointmentStatus);
    setSector(sample.sector);
    setRemarks(sample.remarks);
  }

  /** Which step a server-named field lives on: only the sector is on step one. */
  function stepOf(field: string): number {
    return field.toLowerCase() === 'isgovernmentservice' ? 0 : 1;
  }

  async function save() {
    if (!canSave || appointmentStatus === null) return;

    setSaving(true);
    setError(null);

    const request = {
      effectiveFrom,
      effectiveTo: effectiveTo || null,
      positionTitle: positionTitle.trim(),
      agencyName: agencyName.trim(),
      monthlySalary: salaryValue,
      salaryGrade: salaryGrade.trim() || null,
      stepIncrement: stepIncrement.trim() || null,
      appointmentStatus,
      isGovernmentService: sector === 'government',
      remarks: remarks.trim() || null,
    };

    try {
      if (editing) await personnelApi.updatePreviousEmployment(personnelId, editing.id, request);
      else await personnelApi.addPreviousEmployment(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        `<RadioGroup>` puts its id on each OPTION, so the derived id resolves to nothing — the
        one field needing the map. `stepOf`/`onStep` only while stepped: on the flat edit form
        every field is already in the DOM.
      */
      focusValidationError(failure, {
        idPrefix: 'previous-employment',
        fieldIds: { isGovernmentService: 'previous-employment-is-government-service-government' },
        ...(stepped ? { stepOf, onStep: setStep } : {}),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit previous employment' : 'Add previous employment'}
      size="2xl"
      id="previous-employment-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={
        confirmShowing
          ? { id: 'previous-employment-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }
          : undefined
      }
      footer={
        <>
          <Button id="previous-employment-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {stepped && stepIndex > 0 ? (
            <Button
              id="previous-employment-back"
              variant="secondary"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {stepped && !isLastStep ? (
            <Button
              id="previous-employment-next"
              variant="secondary"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!steps[stepIndex].valid}
            >
              Next
            </Button>
          ) : null}

          <Button
            id="previous-employment-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {stepped ? (
          <>
            <Stepper
              id="previous-employment"
              steps={steps.map(({ key, label }) => ({ key, label }))}
              activeIndex={stepIndex}
              onJump={(index) => {
                // Forward movement requires every earlier step to be valid; back is free.
                if (index <= stepIndex || steps.slice(0, index).every((s) => s.valid)) {
                  setStep(index);
                }
              }}
            />

            <div className="pt-2">{steps[stepIndex].content}</div>
          </>
        ) : (
          <>
            {sectorFields}
            {detailsFields}
          </>
        )}
      </div>
    </Modal>
  );
}
